import React from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/authContext';
import { useResourceUsage } from '../../../context/ResourceUsageContext';
import { client, unwrap } from '../../adapters/client';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  duration: string;
}

export function PaymentsSettingsTab() {
  const { planData } = useAuth();
  const { usage } = useResourceUsage();
  const [plans, setPlans] = React.useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = React.useState(true);
  const [busy, setBusy] = React.useState<string | null>(null); // plan id or 'cancel'

  React.useEffect(() => {
    let cancelled = false;
    client.get('/public/subscriptionList')
      .then((res) => {
        if (cancelled) return;
        const data = unwrap<any>(res.data);
        setPlans(data?.subscriptionList ?? []);
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load plans'); })
      .finally(() => { if (!cancelled) setPlansLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    setBusy(plan.id);
    try {
      if (plan.id === '1') {
        await client.get('/user/start-free-trial', { params: { priceId: plan.id } });
        toast.success('Free trial started');
        window.location.reload();
      } else {
        const res = await client.get('/payment/generate-payment-link', { params: { priceId: plan.id } });
        const data = unwrap<any>(res.data);
        if (data?.url) {
          window.location.href = data.url;
        } else {
          toast.error('Could not generate payment link');
        }
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.msg || 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? It will remain active until the end of the current billing period.')) return;
    setBusy('cancel');
    try {
      const res = await client.get('/payment/generate-cancel-subscription-link');
      const data = unwrap<any>(res.data);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not open cancellation portal');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.msg || 'Failed to cancel subscription');
    } finally {
      setBusy(null);
    }
  };

  const isCurrentPlan = (plan: SubscriptionPlan) =>
    planData?.subscriptionPlanId != null && String(planData.subscriptionPlanId) === String(plan.id);

  const isDowngrade = (plan: SubscriptionPlan) => {
    if (!planData?.planPrice) return false;
    const current = parseFloat(String(planData.planPrice).replace('$', ''));
    return plan.price < current;
  };

  const renewalDate = usage.renewalDate || planData?.planEndDate || '—';
  const isActive = planData?.isPlanActive ?? false;
  const cancelPending = planData?.cancelAtPeriodEnd ?? false;
  const planName = usage.planName || planData?.planName?.toString() || 'Free';
  const hasPaidPlan = isActive && planData?.subscriptionPlanId !== '1' && planData?.subscriptionPlanId != null;

  return (
    <div className="space-y-8">
      {/* Current plan summary */}
      <section className="bg-white border border-brand-navy/10 rounded-lg shadow-sm p-6">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <h2 className="text-base font-semibold text-brand-navy">Billing & Payments</h2>
          {hasPaidPlan && !cancelPending && (
            <button
              onClick={handleCancel}
              disabled={busy === 'cancel'}
              className="px-4 py-1.5 text-sm rounded border border-red-200 text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 disabled:opacity-50"
            >
              {busy === 'cancel' ? 'Opening portal…' : 'Cancel Subscription'}
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-brand-navy/10 p-4">
            <div className="text-xs text-gray-500">Current Plan</div>
            <div className="mt-1 text-lg font-semibold text-brand-navy">{planName}</div>
            <div className="mt-1 text-xs">
              Status:{' '}
              <span className={isActive ? 'text-green-600' : 'text-red-500'}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {cancelPending && (
              <div className="mt-1 text-xs text-amber-600">Cancellation scheduled</div>
            )}
          </div>
          <div className="rounded-lg border border-brand-navy/10 p-4">
            <div className="text-xs text-gray-500">Notes Used</div>
            <div className="mt-1 text-lg font-semibold text-brand-navy">{usage.notesCount}</div>
          </div>
          <div className="rounded-lg border border-brand-navy/10 p-4">
            <div className="text-xs text-gray-500">Renewal Date</div>
            <div className="mt-1 text-lg font-semibold text-brand-navy">{renewalDate}</div>
          </div>
        </div>
      </section>

      {/* Plan selection */}
      <section className="bg-white border border-brand-navy/10 rounded-lg shadow-sm p-6">
        <h2 className="text-base font-semibold text-brand-navy mb-1">Choose Your Plan</h2>
        <p className="text-sm text-brand-navy/60 mb-6">Select the subscription that best fits your needs</p>

        {plansLoading && <p className="text-sm text-brand-navy/60">Loading plans…</p>}

        {!plansLoading && plans.length === 0 && (
          <p className="text-sm text-gray-500">No plans available at the moment.</p>
        )}

        {!plansLoading && plans.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {plans.map((plan) => {
              const current = isCurrentPlan(plan);
              const downgrade = isDowngrade(plan);
              return (
                <div
                  key={plan.id}
                  className={`rounded-lg border p-5 flex flex-col ${current ? 'border-brand-navy ring-1 ring-brand-navy bg-brand-navy/5' : 'border-brand-navy/10'}`}
                >
                  {current && (
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-brand-navy mb-2">
                      Current Plan
                    </div>
                  )}
                  <h3 className="text-sm font-semibold text-brand-navy">{plan.name}</h3>
                  <div className="mt-2 text-2xl font-bold text-brand-navy">
                    CAD {plan.price}
                    <span className="text-xs font-normal text-brand-navy/50"> / {plan.duration}</span>
                  </div>
                  <div
                    className="mt-3 text-xs text-brand-navy/60 flex-1 [&_.planDescription]:flex [&_.planDescription]:items-center [&_.planDescription]:gap-1 [&_.planDescription]:py-0.5"
                    dangerouslySetInnerHTML={{ __html: plan.description }}
                  />
                  <button
                    onClick={() => { if (!current && !downgrade) handleSubscribe(plan); }}
                    disabled={current || downgrade || !!busy}
                    className={`mt-4 w-full rounded-md px-3 py-2 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-brand-accent ${
                      current
                        ? 'bg-brand-navy/10 text-brand-navy/50 cursor-default'
                        : downgrade
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50'
                    }`}
                  >
                    {current
                      ? 'Subscribed'
                      : downgrade
                      ? 'Lower than current plan'
                      : busy === plan.id
                      ? 'Processing…'
                      : 'Subscribe'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
